import React, { useState } from 'react'
import { TConfigUI } from 'src/types/followup'
import { CurecallMedEventButton } from '@honestica/curecall-ui'
import { EMedEventTypes, TMedEventUI } from 'src/types/med-event'
import FollowupMedEventForm from './med-event-form.component'
import { useFollowupFormContext } from 'src/components/followup/generic-followup-form.component'
import { useTranslation } from 'react-i18next'
import {
  canAddMedEventToPatientFollowup,
  getErrorMessageIfMinIsNotReached,
} from '../generic-followup-form.helper'
import { useCurrentProject } from 'src/providers/current-project.provider'

const OptionalMedEventButtonCards = () => {
  const { t } = useTranslation()
  const { configUI, optionalMedEvents, validationErrors, completedMedEvents } = useFollowupFormContext()

  if (!optionalMedEvents || !completedMedEvents) {
    return null
  }

  const renderMedEventsOfType = (type: EMedEventTypes) => {
    const optionalMedEventsOfTypeThatCanBeAdded = optionalMedEvents
      .filter((medEventUI) => medEventUI?.MedEvent.type === type)
      .filter((medEventUI) =>
        canAddMedEventToPatientFollowup(completedMedEvents, medEventUI, configUI as TConfigUI),
      )

    if (optionalMedEventsOfTypeThatCanBeAdded.length < 1) {
      return null
    }

    const errorMessage = getErrorMessageIfMinIsNotReached(t, type, validationErrors)

    return (
      <OptionalMedEventOfType
        type={type}
        restOfTheList={optionalMedEventsOfTypeThatCanBeAdded}
        errorMessage={errorMessage}
      />
    )
  }

  return Object.values(EMedEventTypes).map((type) => {
    const result = renderMedEventsOfType(type)
    return result ? <div key={`${type}`}>{result}</div> : null
  })
}

export interface OptionalMedEventOfTypeProps {
  type: string
  restOfTheList: TMedEventUI[]
  errorMessage?: string
}

const OptionalMedEventOfType = ({ type, restOfTheList, errorMessage }: OptionalMedEventOfTypeProps) => {
  const { t } = useTranslation()

  const { selectNewMedEventToComplete } = useFollowupFormContext()
  const { getMedEventLabelById } = useCurrentProject()

  const [isEdited, setIsEdited] = useState<boolean>(false)
  const [isOptionsShowed] = useState<boolean>(false)

  const handleOnClickOnOption = (event: React.ChangeEvent<HTMLInputElement>) => {
    const choosenMedEventId = event.target.value
    const option = restOfTheList.find((medEventUI) => `${medEventUI.MedEvent.id}` === choosenMedEventId)
    if (option) {
      const isSelected = selectNewMedEventToComplete?.(option)
      isSelected && setIsEdited(true)
    }
  }

  if (isEdited) {
    return (
      <FollowupMedEventForm
        onCloseCallback={() => {
          setIsEdited(false)
        }}
        key={`optional-${type}-medevent-selected`}
      />
    )
  }

  return (
    <div className="c-relative c-flex c-flex-col c-gap-8" key={`optional-${type}-medevent`}>
      {restOfTheList.length === 1 && restOfTheList[0] && (
        <div className={errorMessage ? 'c-mb-4' : ''}>
          <CurecallMedEventButton
            name={getMedEventLabelById(restOfTheList[0].MedEvent.id)}
            onClick={() => {
              const isEdited = selectNewMedEventToComplete?.(restOfTheList[0] as TMedEventUI)
              isEdited && setIsEdited(true)
            }}
            errorMessage={errorMessage}
          />
        </div>
      )}
      {restOfTheList.length > 1 && (
        <div className={errorMessage ? 'c-mb-4' : ''}>
          <CurecallMedEventButton
            name={
              type === EMedEventTypes.SURGERY
                ? t('components.generic-followups-form.add-surgery')
                : t('components.generic-followups-form.add-consultation')
            }
            onClickOnOption={handleOnClickOnOption}
            options={restOfTheList.map((medEvent) => {
              return {
                label: getMedEventLabelById(medEvent.MedEvent.id),
                value: `${medEvent.MedEvent.id}`,
              }
            })}
            errorMessage={errorMessage}
            isOptionsShowed={isOptionsShowed}
          />
        </div>
      )}
    </div>
  )
}

export default OptionalMedEventButtonCards
